import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useClinicSdEnabled,
  useUpdateSdSettings,
  useGetSubsidies,
} from '@/hooks/useSygeforsikring';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { IntegrationsBackLink } from '@/pages/settings/ClinicIntegrations';
import { SettingsLoading } from './settingsChrome';

export function ClinicSygeforsikring() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isPlatformAdmin, isClinicAdmin } = useAuth();

  const { data: sdInfo, isLoading, error } = useClinicSdEnabled(activeClinicId);
  const updateSettings = useUpdateSdSettings();
  const testConnection = useGetSubsidies();

  const [enabled, setEnabled] = useState(false);
  const [providerId, setProviderId] = useState('');
  const [dirty, setDirty] = useState(false);
  const [testCpr, setTestCpr] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (!sdInfo) return;
    setEnabled(sdInfo.enabled);
    setProviderId(sdInfo.providerId ?? '');
    setDirty(false);
  }, [sdInfo]);

  const canAdmin = Boolean(
    activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId ?? '')),
  );

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!activeClinicId) return;

    if (enabled && !providerId.trim()) {
      toast.error(t('sd.settings_provider_id_required'));
      return;
    }

    try {
      await updateSettings.mutateAsync({
        clinic_id: activeClinicId,
        sygeforsikring_enabled: enabled,
        sygeforsikring_provider_id: enabled ? providerId.trim() : null,
      });
      toast.success(t('sd.settings_saved'));
      setDirty(false);
    } catch {
      toast.error(t('sd.settings_save_error'));
    }
  };

  const handleTestConnection = async () => {
    if (!activeClinicId || !testCpr.trim()) {
      toast.error(t('sd.test_enter_cpr'));
      return;
    }
    setTestResult(null);
    try {
      const result = await testConnection.mutateAsync({
        clinic_id: activeClinicId,
        cpr: testCpr.trim(),
      });
      if (result.membership.isMember) {
        setTestResult(t('sd.test_success_member', { group: result.membership.group ?? '?' }));
        toast.success(t('sd.test_connection_ok'));
      } else {
        setTestResult(t('sd.test_success_not_member'));
        toast.success(t('sd.test_connection_ok'));
      }
    } catch {
      setTestResult(t('sd.test_connection_failed'));
      toast.error(t('sd.test_connection_failed'));
    }
  };

  if (isLoading) {
    return <SettingsLoading />;
  }

  if (error) {
    return <ErrorAlert message={t('sd.settings_load_error')} />;
  }

  return (
    <div className="space-y-8">
      <IntegrationsBackLink />

      <div>
        <h2 className="text-lg font-semibold text-ds-text">
          {t('settings.integrations.cards.sygeforsikring_title')}
        </h2>
        <p className="mt-1 text-sm text-ds-muted">
          {t('sd.settings_description')}
        </p>
      </div>

      {/* Current status */}
      <div
        className={`flex items-center gap-3 rounded-xl border p-4 ${sdInfo?.enabled ? 'border-ds-green-line bg-ds-green-tint' : 'border-ds-line bg-ds-sunk'}`}
      >
        <ShieldCheck
          className={`h-6 w-6 ${sdInfo?.enabled ? 'text-ds-green' : 'text-ds-muted'}`}
        />
        <div>
          <p className="text-sm font-medium text-ds-text">
            {sdInfo?.enabled ? t('sd.status_enabled') : t('sd.status_disabled')}
          </p>
          {sdInfo?.providerId && (
            <p className="text-xs text-ds-muted">
              {t('sd.provider_id_label')}: <span className="font-mono">{sdInfo.providerId}</span>
            </p>
          )}
        </div>
      </div>

      {/* Settings form — only platform admins can enable; clinic admins can disable */}
      <form
        onSubmit={handleSave}
        className="space-y-6 rounded-xl border border-ds-line bg-ds-card p-6 shadow-card  "
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-ds-text">
              {t('sd.enable_toggle')}
            </p>
            <p className="text-xs text-ds-muted">{t('sd.enable_toggle_desc')}</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            disabled={!canAdmin || (!isPlatformAdmin && !enabled)}
            onClick={() => {
              setEnabled((v) => !v);
              setDirty(true);
            }}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:shadow-ring disabled:cursor-not-allowed disabled:opacity-50 ${enabled ? 'bg-ds-teal' : 'bg-ds-switch-off'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-ds-knob shadow-card transition duration-200 ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>

        {enabled && (
          <Input
            label={t('sd.provider_id_label')}
            value={providerId}
            onChange={(e) => {
              setProviderId(e.target.value);
              setDirty(true);
            }}
            placeholder="12345"
            hint={t('sd.provider_id_hint')}
            disabled={!canAdmin}
          />
        )}

        {!isPlatformAdmin && !enabled && (
          <p className="text-xs text-ds-amber-ink">
            {t('sd.enable_platform_admin_only')}
          </p>
        )}

        {canAdmin && dirty && (
          <div className="flex justify-end">
            <Button type="submit" disabled={updateSettings.isPending}>
              {updateSettings.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t('common.save')}
                </>
              )}
            </Button>
          </div>
        )}
      </form>

      {/* Test connection */}
      {sdInfo?.enabled && (
        <div className="rounded-xl border border-ds-line bg-ds-card p-6 shadow-card  ">
          <h3 className="text-sm font-medium text-ds-text">
            {t('sd.test_connection_title')}
          </h3>
          <p className="mt-1 text-xs text-ds-muted">
            {t('sd.test_connection_desc')}
          </p>
          <div className="mt-4 flex gap-3">
            <Input
              label={t('sd.test_cpr_label')}
              value={testCpr}
              onChange={(e) => setTestCpr(e.target.value)}
              placeholder="1234567890"
              hint={t('sd.test_cpr_hint')}
            />
            <div className="mt-6 shrink-0">
              <Button
                type="button"
                variant="secondary"
                onClick={handleTestConnection}
                disabled={testConnection.isPending || !testCpr.trim()}
              >
                {testConnection.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('sd.test_connection_btn')
                )}
              </Button>
            </div>
          </div>
          {testResult && (
            <p className="mt-3 text-sm text-ds-text">{testResult}</p>
          )}
        </div>
      )}

      {/* System credentials notice */}
      <div className="rounded-xl border border-ds-amber-line bg-ds-amber-tint p-4 text-sm text-ds-amber-ink">
        <p className="font-medium">{t('sd.credentials_notice_title')}</p>
        <p className="mt-1">{t('sd.credentials_notice_body')}</p>
        <ul className="mt-2 list-inside list-disc space-y-0.5 font-mono text-xs">
          <li>SYGEFORSIKRING_SENDER_ID</li>
          <li>SYGEFORSIKRING_TOKEN</li>
          <li>SYGEFORSIKRING_BASE_URL</li>
          <li>SYGEFORSIKRING_IS_PRODUCTION</li>
        </ul>
      </div>
    </div>
  );
}
